/**
 * フロー図の器（`flow-workspace/`）と線の描画（`FlowGutterEdge`）が使う寸法と、器の高さの決め方。
 *
 * 図の中身の配置（レーン帯 × 列の格子）と寸法は、図ごとの描画エンジンが自分で持つ（業務フロー図は
 * `business-flow-diagram/`）。ここに置くのは、どの図にも共通する器の側の値だけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の器の高さの決め方と、器・線の描画が共有する寸法を定める。
 * @graph-connects none
 */

/** 条件ラベルの帯の幅。ラベルはこの幅で折り返す。 @graph-connects none */
export const LABEL_ZONE_WIDTH = 136;

/** レーン帯の外側に足す余白（px）。 @graph-connects none */
export const CANVAS_PADDING_Y = 96;

/** 図の表示枠の最小の高さ（px）。レーンが 1 本でも図として読める高さを確保する。 @graph-connects none */
export const CANVAS_MIN_HEIGHT = 320;

/** 図の表示枠の最大の高さ（px）。これを超えるレーン数では枠の中をスクロールさせる。 @graph-connects none */
export const CANVAS_MAX_HEIGHT = 720;

/**
 * 図の中身の高さ。レーン帯の高さに、帯の外へ出る分の余白を足す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン帯の高さから図の中身の高さを決める。
 * @graph-connects none
 */
export function canvasContentHeightOf(layoutHeight: number): number {
  return layoutHeight + CANVAS_PADDING_Y;
}

/**
 * レーン本数から図の表示枠の高さを決める。
 *
 * 高さを画面比で固定すると、レーンが 2 本のフローでも縦に大きな空白が残り、枠に合わせる
 * 描画（`fitView`）が横幅だけに合わせて縮むため図が小さく描かれる。線が細く潰れて、どの
 * ノードが繋がっているのかを目で追えなくなる。中身の高さに合わせれば、同じ幅でも図が
 * 大きく描かれる。
 *
 * 上限で頭打ちにするのは、レーンが多いフローで画面の縦を図が丸ごと占め、見出しも詳細も
 * 同時に読めなくなるためである。中身が上限を超える分は枠の中をスクロールして読む。全画面の
 * ときは、その上限が画面の高さから決まる（決め方は `paneHeightLimitOf`）。
 *
 * @param layoutHeight レーン帯の高さ
 * @param viewport 全画面かどうかと画面の高さ
 * @returns 図の表示枠の高さ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン帯の高さから図の表示枠の高さを決める。
 * @graph-connects canvasContentHeightOf [calls] 図の中身の高さ
 * @graph-connects paneHeightLimitOf [calls] 表示枠の高さの上限
 */
export function canvasHeightOf(layoutHeight: number, viewport: FlowViewport): number {
  return Math.min(
    Math.max(canvasContentHeightOf(layoutHeight), CANVAS_MIN_HEIGHT),
    paneHeightLimitOf(viewport),
  );
}

/** 図の器の高さの上限を決めるのに要る、画面の状態。 */
export interface FlowViewport {
  /** いま全画面かどうか。 */
  fullscreen: boolean;
  /** 画面（ビューポート）の高さ。 */
  height: number;
}

/**
 * 図の器の高さの**上限**。
 *
 * 通常の表示では固定の上限を置く。図の外に見出し・件数・詳細ペインが並ぶので、図が画面の縦を
 * 丸ごと占めるとそれらと図を同時に読めなくなる。
 *
 * 全画面では図の外に並ぶものが操作列だけになるので、その理由が消える。代わりに上限を**画面の
 * 高さ**から決める。器が画面より高いと、全画面にしたのに覆いごと縦スクロールする形になり、
 * 「画面の広さいっぱいで読む」目的が果たせない。下限は `CANVAS_MIN_HEIGHT` に置いて、画面が
 * 極端に低いときでも図として読める高さを残す。
 *
 * 上限の決め方を `canvasHeightOf` の中に埋めず、ここへ切り出してあるのは、上限そのものが
 * 「中身の高さ」とは別の判断だからである。中身の高さは骨格から決まり、上限は画面の状態から
 * 決まる。混ぜて書くと、全画面のときだけ規則が変わることが式の中に埋もれる。
 *
 * @param viewport 全画面かどうかと画面の高さ
 * @returns 器の高さの上限
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の器の高さの上限を、通常表示なら固定値・全画面なら画面の高さから決める。
 * @graph-connects none
 */
export function paneHeightLimitOf(viewport: FlowViewport): number {
  if (!viewport.fullscreen) return CANVAS_MAX_HEIGHT;
  return Math.max(CANVAS_MIN_HEIGHT, viewport.height - FLOW_FULLSCREEN_CHROME_HEIGHT);
}

/**
 * 全画面表示で、図の器の外に置く分の高さ（px）。覆いの上下の余白・表示の操作列・区画の見出し。
 *
 * 実測値ではなく決め打ちにするのは、これらを実測すると「測った高さから器の高さを決め、その器が
 * 属する覆いの高さがまた観測に入る」という循環になるためである。器の外に置くものはこの実装が
 * 自分で並べており、増減はこのファイルと図を並べる画面を同時に触るときにしか起きない。
 * @graph-connects none
 */
export const FLOW_FULLSCREEN_CHROME_HEIGHT = 176;

/**
 * 図をどこまで小さく描いてよいかの下限。
 *
 * これ以上縮めるとノードの文字が読めず、全体を見る意味が無くなる。既定（0.5）のままだと列が
 * 8 本あるフローは全体が枠に入らないので、そこまで下げる。現在版の図でも PJ から見た図でも
 * 同じ値を使う。2 箇所で別の値を持つと、同じ図が画面によって違う最小の大きさになり、どちらが
 * 正しい大きさかを読み手が判断できない（core-principles の一次表現原則）。
 * @graph-connects none
 */
export const MIN_FIT_SCALE = 0.2;
